import React, { useState } from 'react';
import {
  TextField,
  InputAdornment,
  IconButton,
  Box,
  Stack,
  Chip,
  Card,
  Avatar,
} from '@mui/material';
import { 
  FolderOpen as FolderOpenIcon,
  Clear as ClearIcon,
  Image as ImageIcon,
  InsertDriveFile as FileIcon,
} from '@mui/icons-material';
import { MediaFile, MediaImage } from '../../models/media';
import MediaDialog from './MediaDialog';

interface MediaSelectorProps {
  value?: string;
  onChange: (value: string) => void;
  label?: string;
  required?: boolean;
  disabled?: boolean;
  placeholder?: string;
  fileTypeFilter?: 'all' | 'images' | 'documents';
}

const MediaSelector: React.FC<MediaSelectorProps> = ({
  value = '',
  onChange,
  label = 'File URL',
  required = false,
  disabled = false,
  placeholder = 'Enter URL or select file from media library',
  fileTypeFilter = 'all',
}) => {
  const [dialogOpen, setDialogOpen] = useState(false);
  
  const handleOpenDialog = () => {
    setDialogOpen(true);
  };
  
  const handleCloseDialog = () => {
    setDialogOpen(false);
  };
  
  const handleFileSelect = (file: MediaFile | MediaImage) => {
    // Use the file URL as the field value
    onChange(file.file);
    handleCloseDialog();
  };
  
  // Create a short display name for the selected file, if any
  const getShortFileName = () => {
    if (!value) return '';
    const parts = value.split('/');
    return parts[parts.length - 1];
  };
  // Determine if the current value is an image
  const isImage = value && (
    value.toLowerCase().endsWith('.jpg') || 
    value.toLowerCase().endsWith('.jpeg') || 
    value.toLowerCase().endsWith('.png') || 
    value.toLowerCase().endsWith('.gif') || 
    value.toLowerCase().endsWith('.webp') || 
    value.toLowerCase().endsWith('.svg')
  );
  
  const handleClear = () => {
    onChange('');
  };
  
  return (
    <>
      <Box sx={{ width: '100%' }}>
        <TextField
          fullWidth
          label={label}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          required={required}
          disabled={disabled}
          placeholder={placeholder}
          sx={{
            '& .MuiInputBase-input': {
              textOverflow: 'ellipsis',
              overflow: 'hidden',
              whiteSpace: 'nowrap',
              maxWidth: 'calc(100% - 40px)'
            }
          }}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <IconButton 
                  edge="end" 
                  onClick={handleOpenDialog}
                  disabled={disabled}
                  title="Select from Media Library"
                  size="small"
                >
                  <FolderOpenIcon />
                </IconButton>
              </InputAdornment>
            ),
          }}
        />
        
        {/* Show preview if a file is selected */}
        {value && (
          <Card variant="outlined" sx={{ mt: 2, p: 1 }}>
            <Stack 
              direction="row" 
              spacing={2} 
              alignItems="center" 
              justifyContent="space-between"
              sx={{ width: '100%' }}
            >
              <Stack direction="row" spacing={2} alignItems="center">
                {isImage ? (
                  <Avatar 
                    src={value} 
                    variant="rounded" 
                    sx={{ width: 40, height: 40 }}
                  >
                    <ImageIcon />
                  </Avatar>
                ) : (
                  <Avatar variant="rounded" sx={{ bgcolor: 'action.selected', width: 40, height: 40 }}>
                    <FileIcon />
                  </Avatar>
                )}
                
                <Chip 
                  label={getShortFileName()}
                  variant="outlined"
                  size="small"
                />
              </Stack>
              
              <IconButton 
                size="small" 
                onClick={handleClear}
                disabled={disabled}
                title="Clear selection"
              >
                <ClearIcon fontSize="small" />
              </IconButton>
            </Stack>
          </Card>
        )}
      </Box>
      
      {/* Media Selection Dialog */}
      <MediaDialog
        open={dialogOpen}
        onClose={handleCloseDialog}
        onSelect={handleFileSelect}
        fileTypeFilter={fileTypeFilter}
      />
    </>
  );
};

export default MediaSelector;
